import { OpenCreateDialogParams } from 'components/dataCreationForms/types';
import { submitServerChange } from 'components/dataCreationForms/common';
import { organizationCreateOnViewToOrganizationCreateModel } from 'shared/domain/organization/mapping/toModel';
import { createEmptyOrganization } from 'shared/domain/organization/mapping/toView';
import { organizationClient } from 'shared/domain/organization/organizationClient';
import { OrganizationCreateOnView } from 'shared/domain/organization/types/view';
import { useCancelConfirmation } from 'presentation/dialogForms/dialogFormsHooks';
import {
  ComponentType,
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { getOrganizationsStoreState } from 'store/organizations';
import { getUserStoreState, useCurrentUser } from 'store/user';
import { debugLog } from 'shared/logger/debugLog';
import { CreateOrganizationContext } from '../types';
import {
  handleOrganizationCreateFormValidation,
  validateOrganizationCreate,
} from '../validation';

const SUBMIT_EVENT_TYPE = 'submit-organization';
const formName = 'organization';

const WithCreateOrganizationContext = createContext<
  CreateOrganizationContext | undefined
>(undefined);

function WithCreateOrganization({
  children,
  onSuccessCallback,
}: {
  children: ReactElement;
  onSuccessCallback?: (_id: string, label: string) => void;
}): ReactElement {
  const { email: contactEmail } = useCurrentUser();

  const [initialValues, setInitialValues] = useState(
    createEmptyOrganization(contactEmail)
  );
  const releaseSubmitEvent = useCallback(() => {
    window.dispatchEvent(new CustomEvent(SUBMIT_EVENT_TYPE));
  }, []);
  const [open, setOpen] = useState(false);

  const openDialog = useCallback(
    ({ freeSoloLabel }: OpenCreateDialogParams) => {
      setOpen(true);

      initialValues &&
        freeSoloLabel &&
        setInitialValues({
          ...initialValues,
          label: freeSoloLabel,
          name: freeSoloLabel,
        });
    },
    [initialValues]
  );
  const confirmCancel = useCancelConfirmation(formName);
  const closeDialog = useCallback(async () => {
    const { cancelConfirmed } = await confirmCancel();
    if (!cancelConfirmed) {
      return;
    }
    setOpen(false);
  }, [confirmCancel]);
  const [isPosting, setIsPosting] = useState(false);

  const submitForm = useCallback(
    async (values: OrganizationCreateOnView) => {
      const outcome = await submitServerChange({
        change: () =>
          organizationClient.create(
            organizationCreateOnViewToOrganizationCreateModel(values)
          ),
        setOpen,
        setIsPosting,
        action: 'creating an organization',
      });
      if (!outcome.saved) {
        return;
      }

      const organization = outcome.result;
      // The project form's organization picker needs the new organization and
      // the user's admin permission in it before `onSuccessCallback` selects it.
      await Promise.all([
        getOrganizationsStoreState().actions.reload(),
        getUserStoreState().actions.refresh().catch((error: unknown) => {
          debugLog('Failed to refresh user permissions after organization create', error);
        }),
      ]);
      onSuccessCallback?.(organization._id, organization.label || values.label);

      return organization;
    },
    [onSuccessCallback]
  );

  const ctx: CreateOrganizationContext = useMemo(() => {
    return {
      open,
      openDialog,
      closeDialog,
      submitForm,
      isPosting,
      initialValues,
      SUBMIT_EVENT_TYPE,
      releaseSubmitEvent,
      validate: validateOrganizationCreate,
      handleFormValidation: handleOrganizationCreateFormValidation,
      formName,
    };
  }, [
    closeDialog,
    initialValues,
    isPosting,
    open,
    openDialog,
    releaseSubmitEvent,
    submitForm,
  ]);

  return (
    <WithCreateOrganizationContext.Provider value={ctx}>
      {children}
    </WithCreateOrganizationContext.Provider>
  );
}

function useCreateOrganization(): CreateOrganizationContext {
  const context = useContext(WithCreateOrganizationContext);
  if (context === undefined) {
    throw new Error(
      'useCreateOrganization must be used within an CreateOrganizationContext'
    );
  }
  return context;
}

const withCreateOrganization =
  (Component: ComponentType<any>) =>
  ({ ...props }): ReactElement => (
    <WithCreateOrganization>
      <Component {...props} />
    </WithCreateOrganization>
  );

export {
  WithCreateOrganization,
  useCreateOrganization,
  withCreateOrganization,
};
